import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { FiEdit2, FiExternalLink, FiTrash2, FiUpload } from 'react-icons/fi';
import { Link, useHistory, useParams } from 'react-router-dom';

import { Alert } from '../../../../components/_components_0.2/Alert';
import {
  absoluteReportUrl,
  apiErrorMessage,
  Feed,
  feedAnalysisService,
  FeedSample,
  NutrientKey,
  NUTRIENTS,
  REFERENCED_NUTRIENTS,
} from '../../../../services/feedAnalysisService';
import { StatusValue } from '../components/ResultsGrid';
import {
  formatDate,
  formatDateTime,
  formatDifference,
  formatNumber,
  formatTolerance,
  monthInput,
  monthLong,
  monthShort,
  STATUS_STYLE,
} from '../format';
import { PageShell } from '../PageShell';

const Info: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <Box>
    <Text fontSize="xs" color="gray.400" textTransform="uppercase">
      {label}
    </Text>
    <Text fontWeight="500">{value || '—'}</Text>
  </Box>
);

type EditForm = {
  sample_code: string;
  feed_id: string;
  lot: string;
  collected_on: string;
  analysis_month: string;
  values: Record<NutrientKey, string>;
};

const toText = (value: number | null) =>
  value === null ? '' : String(value).replace('.', ',');

function toNumber(value: string): number | null {
  const text = value.trim();
  if (!text) return null;
  const number = Number(
    text.includes(',') ? text.replace(/\./g, '').replace(',', '.') : text,
  );
  return Number.isFinite(number) ? number : null;
}

const FeedSampleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const toast = useToast();
  const confirmDelete = useDisclosure();
  const [sample, setSample] = useState<FeedSample | null>(null);
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [error, setError] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<EditForm | null>(null);

  const load = useCallback(async () => {
    setError('');
    try {
      const data = await feedAnalysisService.getSample(id);
      setSample(data);
      setNote(data.analyst_note || '');
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'Amostra não encontrada.'));
    }
  }, [id]);

  useEffect(() => {
    load();
    feedAnalysisService
      .me()
      .then(access => setIsAdmin(access.isAdmin))
      .catch(() => setIsAdmin(false));
    feedAnalysisService
      .listFeeds()
      .then(setFeeds)
      .catch(() => setFeeds([]));
  }, [load]);

  const run = async (
    action: () => Promise<FeedSample | void>,
    done: string,
  ) => {
    setBusy(true);
    try {
      const updated = await action();
      if (updated) {
        setSample(updated);
        setNote(updated.analyst_note || '');
      }
      toast({ title: done, status: 'success' });
      return true;
    } catch (requestError) {
      toast({
        title: apiErrorMessage(requestError, 'Não foi possível concluir.'),
        status: 'error',
      });
      return false;
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (current: FeedSample) =>
    setForm({
      sample_code: current.sample_code,
      feed_id: current.feed_id,
      lot: current.lot || '',
      collected_on: current.collected_on || '',
      analysis_month: monthInput(current.analysis_month),
      values: Object.fromEntries(
        NUTRIENTS.map(item => [item.key, toText(current[item.key])]),
      ) as Record<NutrientKey, string>,
    });

  const saveEdit = async (current: FeedSample, data: EditForm) => {
    const ok = await run(
      () =>
        feedAnalysisService.updateSample(current.id, {
          sample_code: data.sample_code.trim(),
          feed_id: data.feed_id,
          item_name: current.item_name,
          lot: data.lot.trim() || null,
          manufactured_on: current.manufactured_on,
          collected_on: data.collected_on || null,
          received_on: current.received_on,
          released_at: current.released_at
            ? current.released_at.slice(0, 16)
            : null,
          laboratory: current.laboratory,
          analysis_month: data.analysis_month,
          ...(Object.fromEntries(
            NUTRIENTS.map(item => [item.key, toNumber(data.values[item.key])]),
          ) as Record<NutrientKey, number | null>),
        }),
      'Amostra atualizada.',
    );
    if (ok) setForm(null);
  };

  const uploadPdf = (current: FeedSample, file?: File) => {
    if (!file) return;
    run(
      () => feedAnalysisService.uploadReport(current.id, file),
      'PDF do laudo anexado.',
    );
  };

  const remove = async (current: FeedSample) => {
    confirmDelete.onClose();
    const ok = await run(
      () => feedAnalysisService.deleteSample(current.id),
      'Amostra excluída.',
    );
    if (ok)
      history.push(
        `/feed-analysis?month=${monthInput(current.analysis_month)}`,
      );
  };

  if (error) {
    return (
      <PageShell title="Amostra">
        <Text color="red.400">{error}</Text>
      </PageShell>
    );
  }

  if (!sample) {
    return (
      <PageShell title="Amostra">
        <Flex justify="center" py="10">
          <Spinner />
        </Flex>
      </PageShell>
    );
  }

  const reportUrl = absoluteReportUrl(sample.report_url);
  const outOfRange = sample.summary.below + sample.summary.above;
  let reviewText = 'Todos os nutrientes avaliados estão dentro da faixa.';
  if (sample.reviewed_at) {
    reviewText = `Registrado por ${
      sample.reviewed_by_name || '—'
    } em ${formatDateTime(sample.reviewed_at)}.`;
  } else if (outOfRange) {
    reviewText = `${outOfRange} nutriente(s) fora da faixa. O parecer tira a amostra dos alertas do mês.`;
  }

  const actions = (
    <>
      <Button
        as={Link}
        to={`/feed-analysis?month=${monthInput(sample.analysis_month)}`}
        size="sm"
        variant="outline"
      >
        Voltar ao mês
      </Button>
      {reportUrl && (
        <Button
          as="a"
          href={reportUrl}
          target="_blank"
          rel="noopener noreferrer"
          size="sm"
          leftIcon={<FiExternalLink />}
        >
          Abrir PDF
        </Button>
      )}
      <Button
        as="label"
        size="sm"
        leftIcon={<FiUpload />}
        cursor="pointer"
        isDisabled={busy}
      >
        {reportUrl ? 'Trocar PDF' : 'Anexar PDF'}
        <input
          type="file"
          accept="application/pdf"
          hidden
          onChange={event => uploadPdf(sample, event.target.files?.[0])}
        />
      </Button>
      {!form && (
        <Button
          size="sm"
          leftIcon={<FiEdit2 />}
          onClick={() => startEdit(sample)}
        >
          Editar
        </Button>
      )}
      {isAdmin && (
        <Button
          size="sm"
          colorScheme="red"
          variant="ghost"
          leftIcon={<FiTrash2 />}
          onClick={confirmDelete.onOpen}
        >
          Excluir
        </Button>
      )}
    </>
  );

  return (
    <PageShell
      title={`${sample.feed_name} · ${sample.sample_code}`}
      subtitle={`Análises de ${monthLong(sample.analysis_month)}`}
      actions={actions}
    >
      <Alert
        isOpen={confirmDelete.isOpen}
        onClose={confirmDelete.onClose}
        handleFunction={() => remove(sample)}
        title="Excluir amostra"
        textBody={`A amostra ${sample.sample_code} e o PDF anexado serão apagados. Deseja continuar?`}
        ButonSave="Excluir"
        ButonClose="Cancelar"
      />

      <SimpleGrid
        columns={{ base: 2, md: 4 }}
        spacing="4"
        bg="gray.800"
        borderWidth="1px"
        borderColor="gray.700"
        borderRadius="lg"
        p="4"
        mb="6"
      >
        <Info label="Fórmula" value={sample.formula_code || 'Sem fórmula'} />
        <Info
          label="Referência"
          value={
            sample.reference
              ? `Vigência ${monthShort(
                  sample.reference.valid_from,
                )} · ${formatTolerance(
                  sample.reference.tolerance_below,
                  sample.reference.tolerance_above,
                )}`
              : 'Sem referência no mês'
          }
        />
        <Info label="Lote" value={sample.lot} />
        <Info label="Item no laudo" value={sample.item_name} />
        <Info label="Fabricação" value={formatDate(sample.manufactured_on)} />
        <Info label="Coleta" value={formatDate(sample.collected_on)} />
        <Info label="Recebimento" value={formatDate(sample.received_on)} />
        <Info label="Liberação" value={formatDateTime(sample.released_at)} />
        <Info label="Laboratório" value={sample.laboratory} />
        <Info label="Importado por" value={sample.created_by_name} />
        <Info
          label="PDF"
          value={sample.report_original_name || 'Não anexado'}
        />
      </SimpleGrid>

      {form ? (
        <Box
          bg="gray.800"
          borderWidth="1px"
          borderColor="blue.500"
          borderRadius="lg"
          p="4"
          mb="6"
        >
          <Heading size="sm" mb="4">
            Editar amostra
          </Heading>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 5 }} spacing="3" mb="4">
            <FormControl isRequired>
              <FormLabel fontSize="xs">Código da amostra</FormLabel>
              <Input
                size="sm"
                value={form.sample_code}
                onChange={event => {
                  setForm({ ...form, sample_code: event.target.value });
                }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="xs">Ração</FormLabel>
              <Select
                size="sm"
                value={form.feed_id}
                onChange={event => {
                  setForm({ ...form, feed_id: event.target.value });
                }}
              >
                {feeds.map(feed => (
                  <option key={feed.id} value={feed.id}>
                    {feed.name}
                  </option>
                ))}
              </Select>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Lote</FormLabel>
              <Input
                size="sm"
                value={form.lot}
                onChange={event => {
                  setForm({ ...form, lot: event.target.value });
                }}
              />
            </FormControl>
            <FormControl>
              <FormLabel fontSize="xs">Data de coleta</FormLabel>
              <Input
                size="sm"
                type="date"
                value={form.collected_on}
                onChange={event => {
                  setForm({ ...form, collected_on: event.target.value });
                }}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel fontSize="xs">Mês das análises</FormLabel>
              <Input
                size="sm"
                type="month"
                value={form.analysis_month}
                onChange={event => {
                  setForm({ ...form, analysis_month: event.target.value });
                }}
              />
            </FormControl>
          </SimpleGrid>
          <SimpleGrid
            columns={{ base: 2, sm: 3, md: 5, xl: 9 }}
            spacing="3"
            mb="4"
          >
            {NUTRIENTS.map(nutrient => (
              <FormControl key={nutrient.key}>
                <FormLabel fontSize="xs" isTruncated>
                  {nutrient.label}
                </FormLabel>
                <Input
                  size="sm"
                  inputMode="decimal"
                  value={form.values[nutrient.key]}
                  onChange={event => {
                    setForm({
                      ...form,
                      values: {
                        ...form.values,
                        [nutrient.key]: event.target.value,
                      },
                    });
                  }}
                />
              </FormControl>
            ))}
          </SimpleGrid>
          <Flex gridGap="2" justify="flex-end">
            <Button size="sm" variant="ghost" onClick={() => setForm(null)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              colorScheme="blue"
              isLoading={busy}
              onClick={() => saveEdit(sample, form)}
            >
              Salvar
            </Button>
          </Flex>
        </Box>
      ) : (
        <Box
          bg="gray.800"
          borderWidth="1px"
          borderColor="gray.700"
          borderRadius="lg"
          overflowX="auto"
          mb="6"
        >
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>Nutriente</Th>
                <Th textAlign="center">Resultado</Th>
                <Th isNumeric>Mínimo</Th>
                <Th isNumeric color="blue.300">
                  Esperado
                </Th>
                <Th isNumeric>Máximo</Th>
                <Th isNumeric>Diferença</Th>
                <Th>Situação</Th>
              </Tr>
            </Thead>
            <Tbody>
              {REFERENCED_NUTRIENTS.map(nutrient => {
                const item = sample.evaluation[nutrient.key];
                const style = item.status ? STATUS_STYLE[item.status] : null;
                return (
                  <Tr key={nutrient.key}>
                    <Td fontWeight="600">{nutrient.label}</Td>
                    <Td textAlign="center">
                      <StatusValue item={item} />
                    </Td>
                    <Td isNumeric>{formatNumber(item.min)}</Td>
                    <Td isNumeric color="blue.300">
                      {formatNumber(item.expected)}
                    </Td>
                    <Td isNumeric>{formatNumber(item.max)}</Td>
                    <Td isNumeric>{formatDifference(item.difference)}</Td>
                    <Td>
                      {style && (
                        <Badge colorScheme={style.scheme}>
                          {style.symbol} {style.label}
                        </Badge>
                      )}
                    </Td>
                  </Tr>
                );
              })}
              <Tr>
                <Td fontWeight="600">DGM (µm)</Td>
                <Td textAlign="center">{formatNumber(sample.dgm, 0)}</Td>
                <Td colSpan={5} color="gray.500">
                  Sem referência (só registro)
                </Td>
              </Tr>
              <Tr>
                <Td fontWeight="600">DPG</Td>
                <Td textAlign="center">{formatNumber(sample.dpg)}</Td>
                <Td colSpan={5} color="gray.500">
                  Sem referência (só registro)
                </Td>
              </Tr>
            </Tbody>
          </Table>
        </Box>
      )}

      <Box
        bg="gray.800"
        borderWidth="1px"
        borderColor={
          outOfRange && !sample.reviewed_at ? 'yellow.500' : 'gray.700'
        }
        borderRadius="lg"
        p="4"
      >
        <Heading size="sm" mb="1">
          Parecer do analista
        </Heading>
        <Text fontSize="sm" color="gray.400" mb="3">
          {reviewText}
        </Text>
        <Textarea
          value={note}
          onChange={event => setNote(event.target.value)}
          placeholder="Ex.: umidade baixa por ajuste no secador; nutricionista avisado."
          maxLength={2000}
          mb="3"
        />
        <Flex justify="flex-end">
          <Button
            size="sm"
            colorScheme="blue"
            isLoading={busy}
            onClick={() => {
              run(
                () => feedAnalysisService.reviewSample(sample.id, note),
                'Parecer registrado.',
              );
            }}
          >
            Registrar parecer
          </Button>
        </Flex>
      </Box>
    </PageShell>
  );
};

export default FeedSampleDetail;
